import { withFocusRing } from "@components/Accessibility/FocusRing/FocusRing";
import { Icon } from "@components/Icon";
import { styledWithInvalidProps } from "@components/Styled/StyledWithInvalidProps";
import { FilterContext } from "~/providers/FilterProvider";
import { Filter } from "@usereactify/search";
import React, { useContext, useState } from "react";
import tw from "twin.macro";
import { FilterSection } from "./FilterSection";
import { FilterSectionListSelector } from "./FilterSectionListSelector";
import { FilterSectionProductStats } from "./FilterSectionProductStats";
import { FilterSectionRange } from "./FilterSectionRange";
import { FilterSectionSelectedFilters } from "./FilterSectionSelectedFilters";
import { Button, useProductImage } from "@bared/ui";

export const Wrapper = tw.div`
  relative
`;

const FilterHeader = tw.div`
  flex justify-between items-center mt-pico py-mini px-mini w-full xl:border-b xl:pb-maxi xl:mb-maxi
`;

const FilterHeaderLeft = tw.div`
  flex-none self-end text-left lg:pointer-events-none pointer-events-auto
`;

const FilterHeaderLeftIcon = tw(Icon)`
  mr-micro align-middle
`;

const FilterHeaderLeftTitle = tw.span`
  leading-h3 font-body text-center underline underline-offset-4 xl:no-underline
`;

const FilterSections = styledWithInvalidProps("div")(
  ({ open }: { open: boolean }) => [open ? tw`block` : tw`hidden xl:block`],
);

const FilterContainer = tw.div`
  xl:block top-[122px] overflow-y-auto p-midi
`;
const MobileApplyButton = withFocusRing(tw.button`
  lg:hidden block bg-primary w-full h-giga text-neutral mt-midi
`);

const FilterSelectedMobile = withFocusRing(tw.button`
  lg:hidden block flex w-full
`);

const FilterHeaderModal = tw.div`
  bg-neutral w-full h-[56px] flex items-center px-midi justify-between
`;

const Content = tw.div`
  h-full pt-maxi md:pt-giga
`;

const ModalText = tw.div`text-primary`;

const FiltersModal = styledWithInvalidProps("div")(({ active }) => [
  tw`fixed inset-x-0 bottom-0 z-10 block overflow-x-hidden overflow-y-auto top-[58px] border-tertiary bg-white lg:hidden`,
  active
    ? tw`visible opacity-100 pointer-events-auto`
    : tw`invisible opacity-0 pointer-events-none`,
]);

const RemoveIcon = tw(Icon)`
  cursor-pointer
`;

const TitleWrapper = tw.div`
  flex items-center
`;

const FilterModalStats = tw.div`
  flex flex-row items-center 
`;

export const FiltersMobile = () => {
  const { filters, showViewToggle } = useContext(FilterContext);
  const { showHoverImages, toggleHoverImages } = useProductImage();
  const [show, setShow] = useState(false);

  return (
    <>
      <Wrapper>
        <FilterHeader>
          <TitleWrapper>
            <FilterHeaderLeft onClick={() => setShow((show) => !show)}>
              <FilterHeaderLeftIcon
                name={
                  show ? "filterThreadActiveIcon" : "filterThreadDefaultIcon"
                }
                size="xxs"
              />
              <FilterHeaderLeftTitle>Filters</FilterHeaderLeftTitle>
            </FilterHeaderLeft>
          </TitleWrapper>
          {showViewToggle && (
            <Button
              variant="secondary"
              size="xsmall"
              className="p-0 z-10 w-[95px]"
              onClick={toggleHoverImages}
              aria-label="Toggle hover images"
            >
              {showHoverImages ? "Product View" : "Model View"}
            </Button>
          )}
        </FilterHeader>
      </Wrapper>
      <FiltersModal active={show} setActive={setShow} layout="filters">
        <Content>
          <FilterHeaderModal>
            <FilterModalStats>
              <ModalText className="pr-mini">Filters</ModalText>
              <FilterSectionProductStats />
            </FilterModalStats>
            <RemoveIcon
              name="close"
              size="xsmall"
              onClick={() => setShow((show) => !show)}
            />
          </FilterHeaderModal>
          <FilterContainer>
            <FilterSections open={true}>
              {filters.map((filter) => (
                <FilterSection
                  title={filter.name}
                  handle={filter.handle}
                  key={filter.id}
                >
                  <Filter
                    filter={filter}
                    renderFilterList={({ filter, filterListProps }) => (
                      <FilterSectionListSelector
                        handle={filter.handle}
                        {...filterListProps}
                      />
                    )}
                    renderFilterRange={({ filterRangeProps }) => (
                      <FilterSectionRange
                        handle={filter.handle}
                        {...filterRangeProps}
                      />
                    )}
                    showEmptyFilters={true}
                  />
                </FilterSection>
              ))}
              <FilterSelectedMobile>
                <FilterSectionSelectedFilters layout="wrap" />
              </FilterSelectedMobile>
              <MobileApplyButton onClick={() => setShow((show) => !show)}>
                Apply Filters
              </MobileApplyButton>
            </FilterSections>
          </FilterContainer>
        </Content>
      </FiltersModal>
    </>
  );
};

export default FiltersMobile;
